import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import { IRoleType } from "../../model/role/RoleType";
import CreateRoleTypeInteractor from "../../interactor/roles/CreateRoleTypeInteractor";
import GetRoleTypesInteractor from "../../interactor/roles/GetRoleTypesInteractor";
import UpdateRoleTypeInteractor from "../../interactor/roles/UpdateRoleTypeInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import GetReportingTypesInteractor from "../../interactor/reporting/GetReportingTypesInteractor";
import { IReportingType } from "../../model/reporting/ReportingType";
import GetRoleTypeReportingTypesInteractor from "../../interactor/roles/GetRoleTypeReportingTypesInteractor";
import RoleTypeReportingType, {
  IRoleTypeReportingType
} from "../../model/role/RoleTypeReportingType";
import CreateRoleTypeReportingTypesInteractor from "../../interactor/roles/CreateRoleTypeReportingTypesInteractor";
import RemoveRoleTypeReportingTypesInteractor from "../../interactor/roles/RemoveRoleTypeReportingTypesInteractor";
import { IPositionType } from "../../model/position/PositionType";
import GetRoleTypePositionTypesInteractor from "../../interactor/roles/GetRoleTypePositionTypesInteractor";
import RoleTypePositionType, {
  IRoleTypePositionType
} from "../../model/role/RoleTypePositionType";
import { IFormItem } from "../../model/editform/FormItem";
import CreateRoleTypePositionTypesInteractor from "../../interactor/roles/CreateRoleTypePositionTypesInteractor";
import RemoveRoleTypePositionTypesInteractor from "../../interactor/roles/RemoveRoleTypePositionTypesInteractor";

export interface TRoleTypesPresenter extends TLoadingAwarePresenter {
  roleTypes: IRoleType[] | undefined;
  reportingTypes?: IReportingType[];
  roleTypeReportingTypes?: IRoleTypePositionType[];
  assignedReportingTypeCodes?: string[];
  expandedRoleTypeCode?: string;
  spinnerEnabled?: boolean;
  positionTypes?: IPositionType[];
  roleTypePositionTypes?: IRoleTypePositionType[];
  assignedPositionTypeIds?: number[];
  formItems?: IFormItem[];
}
export interface IRoleTypesPresenter extends TRoleTypesPresenter, TPresentable {
  setRoleTypes(roleType: IRoleType[] | undefined): void;
  createRoleType(roleType: IRoleType): void;
  getRoleTypes(): void;
  updateRoleType(roleType: IRoleType): void;
  getReportingTypes(): void;
  setReportingTypes(reportingTypes: IReportingType[]): void;
  setExpandedRoleTypeCode(roleTypeCode: string): void;
  createRoleTypeReportingTypes(reportingTypeCodes: string[]): void;
  removeRoleTypeReportingTypes(reportingTypeCode: string[]): void;
  setAssignedReportingTypesCodes(assignedReportingTypeCodes: string[]): void;
  clearState(): void;
  setPositionTypes(positionTypes: IPositionType[]): void;
  getExpnadedRowData(roleTypeCode: string): void;
  createRoleTypePositionTypes(positionTypeIds: number[]): void;
  removeRoleTypePositionTypes(positionTypeIds: string[]): void;
  onPositionTypeSelectChange(assignedPositionTypeIds: number[]): void;
  onReportingTypeSelectChange(selectedReportingTypeCodes: string[]): void;
  onRowExpanded(expanded: any, record: any): void;
}

export interface IReportingTypePresentationModel extends IReportingType {
  value: string;
  label: string;
}

export interface IPositionTypePresentationModel extends IPositionType {
  value: number;
  label: string;
}

const defaultState: TRoleTypesPresenter = {
  roleTypes: undefined,
  assignedPositionTypeIds: []
};

const ReportingTypePresentationModelMapper = (
  reportingTypes: IReportingType[]
): IReportingTypePresentationModel[] => {
  return (
    reportingTypes &&
    reportingTypes.map((reportingType) => {
      return {
        ...reportingType,
        value: reportingType.code,
        label: reportingType.code
      };
    })
  );
};

const AssignedReportingTypeKeysMapper = (
  roleTypeReportingTypes: IRoleTypeReportingType[]
): string[] => {
  return roleTypeReportingTypes.map((roleTypeReportingType) => {
    return roleTypeReportingType.reportingTypeCode;
  });
};

const PositionTypePresentationModelMapper = (
  positionTypes: IPositionType[]
): IPositionTypePresentationModel[] => {
  return (
    positionTypes &&
    positionTypes.map((positionType) => {
      return {
        ...positionType,
        value: positionType.id,
        label: positionType.name
      };
    })
  );
};

const AssignedPositionTypeIdsMapper = (
  roleTypePositionTypes: IRoleTypePositionType[]
) => {
  return roleTypePositionTypes.map((roleTypePositionType) => {
    return roleTypePositionType.positionTypeId;
  });
};

const RoleTypesPresenter = withStore<IRoleTypesPresenter, TRoleTypesPresenter>(
  ({ application, store, loader, translate }): IRoleTypesPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TRoleTypesPresenter>();

    loader.start("roleTypesLoader");

    const setRoleTypes = (roleTypes: IRoleType[]) => {
      _store.update({ roleTypes });
      loader.stop("roleTypesLoader");
    };

    const getRoleTypes = async () => {
      try {
        let roleTypes = await _application.container
          .resolve<GetRoleTypesInteractor>("getRoleTypes")
          .execute();
        _store.update({ roleTypes });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const createRoleType = async (roleType: IRoleType) => {
      try {
        await _application.container
          .resolve<CreateRoleTypeInteractor>("createRoleType")
          .execute(roleType);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate("role_types_page.messages.create_roleType_success")
          );
        await getRoleTypes();
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const updateRoleType = async (roleType: IRoleType) => {
      try {
        await _application.container
          .resolve<UpdateRoleTypeInteractor>("updateRoleType")
          .execute(roleType);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate("role_types_page.messages.update_roleType_success")
          );
        await getRoleTypes();
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const getReportingTypes = async () => {
      try {
        let reportingTypes = await _application.container
          .resolve<GetReportingTypesInteractor>("getReportingTypes")
          .execute();
        reportingTypes = ReportingTypePresentationModelMapper(reportingTypes);
        _store.update({ reportingTypes });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const setReportingTypes = (reportingTypes: IReportingType[]) => {
      reportingTypes =
        reportingTypes && ReportingTypePresentationModelMapper(reportingTypes);
      _store.update({ reportingTypes });
    };

    const clearState = () => {
      _store.update({
        assignedReportingTypeCodes: []
      });
    };

    const getRoleTypeReportingTypes = async (roleTypeCode: string) => {
      try {
        let roleTypeReportingTypes = await _application.container
          .resolve<GetRoleTypeReportingTypesInteractor>(
            "getRoleTypeReportingTypes"
          )
          .execute(roleTypeCode);
        let assignedReportingTypeCodes = AssignedReportingTypeKeysMapper(
          roleTypeReportingTypes
        );
        _store.update({
          roleTypeReportingTypes,
          assignedReportingTypeCodes
        });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const setExpandedRoleTypeCode = (expandedRoleTypeCode: string) => {
      _store.update({ expandedRoleTypeCode });
    };

    const createRoleTypeReportingTypes = async (
      reportingTypeCodes: string[]
    ) => {
      let { expandedRoleTypeCode } = _store.getState();
      try {
        let roleTypeReportingTypes =
          getRoleTypeReportingTypesFromReportingTypeCodes(reportingTypeCodes);

        await _application.container
          .resolve<CreateRoleTypeReportingTypesInteractor>(
            "createRoleTypeReportingTypes"
          )
          .execute(roleTypeReportingTypes);
        await getRoleTypeReportingTypes(expandedRoleTypeCode);
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const getRoleTypeReportingTypesFromReportingTypeCodes = (
      reportingTypeCodes: string[]
    ) => {
      let { expandedRoleTypeCode } = _store.getState();

      return reportingTypeCodes.map((reportingTypeCode) => {
        return RoleTypeReportingType({
          id: 0,
          roleTypeCode: expandedRoleTypeCode,
          reportingTypeCode: reportingTypeCode
        });
      });
    };

    const removeRoleTypeReportingTypes = async (
      reportingTypeCodes: string[]
    ) => {
      try {
        let roleTypeReportingTypeIds =
          getRoleTypeReportinTypeIdsFromReportingTypeCodes(reportingTypeCodes);
        await _application.container
          .resolve<RemoveRoleTypeReportingTypesInteractor>(
            "removeRoleTypeReportingTypes"
          )
          .execute(roleTypeReportingTypeIds);
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const getRoleTypeReportinTypeIdsFromReportingTypeCodes = (
      reportingTypeCodes: string[]
    ) => {
      let { roleTypeReportingTypes } = _store.getState();

      let roleTypeReportingTypesIds: string[] = [];

      roleTypeReportingTypes.forEach((roleTypeReportingType) => {
        reportingTypeCodes.forEach((reportingTypeCode) => {
          if (roleTypeReportingType.reportingTypeCode === reportingTypeCode) {
            roleTypeReportingTypesIds.push(roleTypeReportingType.id);
          }
        });
      });
      return roleTypeReportingTypesIds;
    };

    const setAssignedReportingTypesCodes = (
      assignedReportingTypeCodes: string[]
    ) => {
      _store.update({ assignedReportingTypeCodes });
      disableSpinner();
    };

    const setPositionTypes = (positionTypes: IPositionType[]) => {
      positionTypes = PositionTypePresentationModelMapper(positionTypes);
      _store.update({ positionTypes });
      disableSpinner();
    };

    const enableSpinner = () => {
      _store.update({ spinnerEnabled: true });
    };

    const disableSpinner = () => {
      _store.update({ spinnerEnabled: false });
    };

    const getRoleTypePositionTypes = async (roleTypeCode: string) => {
      try {
        let roleTypePositionTypes = await _application.container
          .resolve<GetRoleTypePositionTypesInteractor>(
            "getRoleTypePositionTypes"
          )
          .execute(roleTypeCode);
        let assignedPositionTypeIds = AssignedPositionTypeIdsMapper(
          roleTypePositionTypes
        );
        _store.update({
          roleTypePositionTypes,
          assignedPositionTypeIds
        });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const getExpnadedRowData = async (roleTypeCode: string) => {
      enableSpinner();

      await getRoleTypeReportingTypes(roleTypeCode);
      await getRoleTypePositionTypes(roleTypeCode);

      disableSpinner();
    };

    const createRoleTypePositionTypes = async (postionTypeIds: number[]) => {
      let { expandedRoleTypeCode } = _store.getState();
      try {
        let roleTypePositionTypes =
          getRoleTypePositionTypesFromPositionTypeIds(postionTypeIds);

        await _application.container
          .resolve<CreateRoleTypePositionTypesInteractor>(
            "createRoleTypePositionTypes"
          )
          .execute(roleTypePositionTypes);
        await getRoleTypePositionTypes(expandedRoleTypeCode);
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const getRoleTypePositionTypesFromPositionTypeIds = (
      positionTypeIds: number[]
    ) => {
      let { expandedRoleTypeCode } = _store.getState();

      return positionTypeIds.map((positionTypeId) => {
        return RoleTypePositionType({
          id: 0,
          roleTypeCode: expandedRoleTypeCode,
          positionTypeId: positionTypeId
        });
      });
    };

    const removeRoleTypePositionTypes = async (positionTypeIds: string[]) => {
      try {
        let roleTypePositionTypeIds =
          getRoleTypePositionTypeIdsFromPositionTypeIds(positionTypeIds);
        await _application.container
          .resolve<RemoveRoleTypePositionTypesInteractor>(
            "removeRoleTypePositionTypes"
          )
          .execute(roleTypePositionTypeIds);
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const getRoleTypePositionTypeIdsFromPositionTypeIds = (
      positionTypeIds: string[]
    ) => {
      let { roleTypePositionTypes } = _store.getState();

      let roleTypePositionTypeIds: string[] = [];

      roleTypePositionTypes.forEach((roleTypeReportingType) => {
        positionTypeIds.forEach((positionTypeId) => {
          if (
            roleTypeReportingType.positionTypeId === parseInt(positionTypeId)
          ) {
            roleTypePositionTypeIds.push(roleTypeReportingType.id);
          }
        });
      });
      return roleTypePositionTypeIds;
    };

    const setAssignedPositionTypeIds = (assignedPositionTypeIds: number[]) => {
      _store.update({ assignedPositionTypeIds });
      disableSpinner();
    };

    const onPositionTypeSelectChange = async (
      selectedPositionTypeIds: number[]
    ) => {
      let { assignedPositionTypeIds } = _store.getState();
      enableSpinner();
      if (selectedPositionTypeIds.length > assignedPositionTypeIds.length) {
        let newPositionTypeIds = selectedPositionTypeIds.filter(
          (selectedPositionTypeId) =>
            !assignedPositionTypeIds.includes(selectedPositionTypeId)
        );
        await createRoleTypePositionTypes(newPositionTypeIds);
      } else if (
        selectedPositionTypeIds.length < assignedPositionTypeIds.length
      ) {
        let removedPositionTypeIds = assignedPositionTypeIds.filter(
          (assignedPositionTypeId) =>
            !selectedPositionTypeIds.includes(assignedPositionTypeId)
        );
        await removeRoleTypePositionTypes(removedPositionTypeIds);
      }

      setAssignedPositionTypeIds(selectedPositionTypeIds);
    };

    const onReportingTypeSelectChange = async (
      selectedReportingTypeCodes: string[]
    ) => {
      let { assignedReportingTypeCodes } = _store.getState();
      enableSpinner();
      if (
        selectedReportingTypeCodes.length > assignedReportingTypeCodes.length
      ) {
        let newReportingTypeCodes = selectedReportingTypeCodes.filter(
          (selectedReportingTypeCode) =>
            !assignedReportingTypeCodes.includes(selectedReportingTypeCode)
        );

        await createRoleTypeReportingTypes(newReportingTypeCodes);
      } else if (
        selectedReportingTypeCodes.length < assignedReportingTypeCodes.length
      ) {
        let removedReportingTypeCodes = assignedReportingTypeCodes.filter(
          (assignedReportingTypeCode) =>
            !selectedReportingTypeCodes.includes(assignedReportingTypeCode)
        );

        await removeRoleTypeReportingTypes(removedReportingTypeCodes);
      }

      setAssignedReportingTypesCodes(selectedReportingTypeCodes);
    };

    const onRowExpanded = (expanded: any, record: any) => {
      if (expanded) {
        setExpandedRoleTypeCode(record.code);
        getExpnadedRowData(record.code);
      }
    };

    return {
      ...state,

      store: _store,
      loader,
      application: _application,
      translate,
      setRoleTypes,
      createRoleType,
      getRoleTypes,
      updateRoleType,
      getReportingTypes,
      setReportingTypes,
      setExpandedRoleTypeCode,
      createRoleTypeReportingTypes,
      removeRoleTypeReportingTypes,
      setAssignedReportingTypesCodes,
      clearState,
      setPositionTypes,
      getExpnadedRowData,
      createRoleTypePositionTypes,
      removeRoleTypePositionTypes,
      onPositionTypeSelectChange,
      onReportingTypeSelectChange,
      onRowExpanded
    };
  },
  defaultState
);

export default RoleTypesPresenter;
